import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                               from '../../../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridGroupColumnConfig
}                               from '../../../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                               from '../../../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {nameOf}                 from '../../../../../ap-core/utils/ap-name-of';
import {
  IPpPlanningWizardDetailsData,
  IPpPlanningWizardDetailsFormData,
  IPpPlanningWizardOverviewGridData,
  IPpPlanningWizardSelectionGridData
}                               from '../../pp-planning-wizard.types';
import {BestScanData}           from '../../../../../types/scan.types';
import {
  GetRoundNumericPipeConfig
}                               from '../../../../../ap-utils/pipes/configs/get-round-numeric.pipe.config';
import {Observable}             from 'rxjs';
import {
  ApDynGridEditColumnConfig
}                               from '../../../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {GetRoundNumericPipe}    from '../../../../../ap-utils';
import {ApTranslationService}   from '../../../../../ap-utils/service/ap-translation.service';
import {StringFactory}          from 'ts-tooling';
import {UnitsStore}             from '../../../../../stores/common/units.store';
import {GetRoundNumericService} from '../../../../../ap-utils/service/get-round-numeric.service';
import {SettingsStore}          from '../../../../../stores/base-data/settings.store';
import {LanguageStore}          from '../../../../../stores/translation/language.store';
import {Injectable}             from '@angular/core';
import {
  ApDynGridStatusColumnConfig
}                               from '../../../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  IGridStatus
}                               from '../../../../../ap-interface/interfaces/ap-grid-status.interface';
import IPlantProtectionPlanBook = Data.TaskManagement.IPlantProtectionPlanBook;

@Injectable()
export class OverviewGridColumnsUiProvider {
  constructor(private unitStore: UnitsStore,
              private settingsStore: SettingsStore,
              private languageStore: LanguageStore,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  /**
   * Creates grid column configs for uniform operationMode with worktype 'spraying'
   * @public
   */
  public getUniformSprayingColumns(): ApDynGridColumnConfigBase {
    return new ApDynGridPropertyColumnConfig({
      title: `${this.translationService.translate('Global__AvgShort')} ${this.translationService.translate('Global__Water')} [${this.translationService.translate('Unit_l_per_ha')}]`,
      field: `${nameOf<IPpPlanningWizardOverviewGridData>('PlanBook')}.${nameOf<IPlantProtectionPlanBook>('ApplicationRate')}`,
      width: 100,
      filterable: true,
      headerFilterable: true,
      cssClass: 'right',
      filterType: FilterType.NUMBER,
      tooltip: (dataItem: IPpPlanningWizardOverviewGridData) => this.minMaxAvgTooltipGenerator(dataItem, 'avg'),
    });
  }

  /**
   * Creates grid column configs for target rate operationMode
   * @public
   */
  public getTargetRateColumns(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridGroupColumnConfig({
        title: 'N__UptakeUnit',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Avg_Symbol',
            field: `${nameOf<IPpPlanningWizardOverviewGridData>('SelectionData')}.${nameOf<IPpPlanningWizardSelectionGridData>('ScanData')}.${nameOf<BestScanData>('NUptakeAvg')}`,
            width: 50,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0, GetRoundNumericPipeConfig.NullToEmptyReplaceConfig]
            }],
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: `${nameOf<IPpPlanningWizardOverviewGridData>('SelectionData')}.${nameOf<IPpPlanningWizardSelectionGridData>('ScanData')}.${nameOf<BestScanData>('NUptakeMin')}`,
            width: 50,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0, GetRoundNumericPipeConfig.NullToEmptyReplaceConfig]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: `${nameOf<IPpPlanningWizardOverviewGridData>('SelectionData')}.${nameOf<IPpPlanningWizardSelectionGridData>('ScanData')}.${nameOf<BestScanData>('NUptakeMax')}`,
            width: 50,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0, GetRoundNumericPipeConfig.NullToEmptyReplaceConfig]
            }]
          })
        ]
      }),
      new ApDynGridGroupColumnConfig({
        title: `${this.translationService.translate('Global__Water')} [${this.translationService.translate('Unit_l_per_ha')}]`,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'Global__AvgShort',
            field: `${nameOf<IPpPlanningWizardOverviewGridData>('PlanBook')}.${nameOf<IPlantProtectionPlanBook>('ApplicationRate')}`,
            width: 50,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }],
            columnLoading: nameOf<IPpPlanningWizardOverviewGridData>('ColumnLoading'),
            tooltip: (dataItem: IPpPlanningWizardOverviewGridData) => this.minMaxAvgTooltipGenerator(dataItem, 'avg'),
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: `${nameOf<IPpPlanningWizardOverviewGridData>('PlanBook')}.${nameOf<IPlantProtectionPlanBook>('ApplicationMin')}`,
            width: 50,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }],
            columnLoading: nameOf<IPpPlanningWizardOverviewGridData>('ColumnLoading'),
            tooltip: (dataItem: IPpPlanningWizardOverviewGridData) => this.minMaxAvgTooltipGenerator(dataItem, 'min'),
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: `${nameOf<IPpPlanningWizardOverviewGridData>('PlanBook')}.${nameOf<IPlantProtectionPlanBook>('ApplicationMax')}`,
            width: 50,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }],
            columnLoading: nameOf<IPpPlanningWizardOverviewGridData>('ColumnLoading'),
            tooltip: (dataItem: IPpPlanningWizardOverviewGridData) => this.minMaxAvgTooltipGenerator(dataItem, 'max'),
          })
        ]
      })
    ];
  }

  /**
   * Creates grid column configs which are common for all operation modes.
   * @param mapHide
   * @public
   */
  public getCommonColumns(mapHide: Observable<boolean>): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({
        field: `${nameOf<IPpPlanningWizardOverviewGridData>('Status')}.${nameOf<IGridStatus>('state')}`,
        class: `${nameOf<IPpPlanningWizardOverviewGridData>('Status')}.${nameOf<IGridStatus>('class')}`,
        description: `${nameOf<IPpPlanningWizardOverviewGridData>('Status')}.${nameOf<IGridStatus>('description')}`,
        filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Number',
        field: `${nameOf<IPpPlanningWizardOverviewGridData>('SelectionData')}.${nameOf<IPpPlanningWizardSelectionGridData>('Nr')}`,
        width: 40,
        hide: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini_Subnumber',
        field: `${nameOf<IPpPlanningWizardOverviewGridData>('SelectionData')}.${nameOf<IPpPlanningWizardSelectionGridData>('TNr')}`,
        width: 40,
        hide: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Number',
        field: `${nameOf<IPpPlanningWizardOverviewGridData>('SelectionData')}.${nameOf<IPpPlanningWizardSelectionGridData>('NrTNr')}`,
        width: 50,
        headerFilterable: true,
        hide: new ApDynGridColumnHideConfig({
          selfHide: mapHide
        })
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Name',
        field: `${nameOf<IPpPlanningWizardOverviewGridData>('SelectionData')}.${nameOf<IPpPlanningWizardSelectionGridData>('Name')}`,
        width: 110,
        cssClass: 'left',
        sortIndex: 0,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Area',
        field: `${nameOf<IPpPlanningWizardOverviewGridData>('SelectionData')}.${nameOf<IPpPlanningWizardSelectionGridData>('Area')}`,
        width: 50,
        headerFilterable: true,
        tooltip: (dataItem) => this.roundNumericService.stringify(((dataItem as IPpPlanningWizardOverviewGridData)?.SelectionData as IPpPlanningWizardSelectionGridData).Area, 4),
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [this.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Crop',
        field: `${nameOf<IPpPlanningWizardOverviewGridData>('SelectionData')}.${nameOf<IPpPlanningWizardSelectionGridData>('Crop')}`,
        width: 80,
        hide: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__EC',
        field: `${nameOf<IPpPlanningWizardOverviewGridData>('DetailsData')}.${nameOf<IPpPlanningWizardDetailsData>('FormData')}.${nameOf<IPpPlanningWizardDetailsFormData>('ECStage')}`,
        width: 50,
        hide: true,
        cssClass: 'right',
        filterType: FilterType.NUMBER,
      })
    ];
  }

  /**
   * Creates grid column configs for the common last columns
   * @public
   */
  public getCommonEndColumns(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Products',
        field: nameOf<IPpPlanningWizardOverviewGridData>('Products'),
        hide: true,
        width: 200,
        tooltip: (dataItem) => (dataItem as IPpPlanningWizardOverviewGridData)?.ProductsTooltip,
        multiline: true,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Base__SortItemDate',
        field: `${nameOf<IPpPlanningWizardOverviewGridData>('DetailsData')}.${nameOf<IPpPlanningWizardDetailsData>('FormData')}.${nameOf<IPpPlanningWizardDetailsFormData>('ApplicationDate')}`,
        format: this.languageStore?.CurrentDateFormat,
        hide: true,
        width: 70
      }),
      new ApDynGridGroupColumnConfig({
        title: 'agrioptions_resources',
        hide: true,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IPpPlanningWizardOverviewGridData>('Machine'),
            title: 'Base__Engine',
            width: 100,
            hide: true,
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IPpPlanningWizardOverviewGridData>('Instrument'),
            title: 'AgriConnect_Ini__Tool',
            width: 100,
            hide: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IPpPlanningWizardOverviewGridData>('Driver'),
            title: 'Docu_Ini__Motorist',
            width: 100,
            hide: true
          })
        ]
      }),
      new ApDynGridEditColumnConfig({})
    ];
  }

  /**
   * Generates tooltip text for minimum and maximum values in the grid
   * Shows product calculations when no errors exist, otherwise displays error message
   * @param dataItem The grid data item containing product and application information
   * @param whichPropertyToUse Specifies whether to calculate for 'min' or 'max' values
   * @returns Formatted tooltip string with product calculations or error message
   * @private
   */
  private minMaxAvgTooltipGenerator(dataItem: IPpPlanningWizardOverviewGridData, whichPropertyToUse: 'min' | 'max' | 'avg'): string {
    if (StringFactory.IsNullOrEmpty(dataItem.ErrorMessage)) {
      const planBook = dataItem?.PlanBook;
      return planBook.ProductList.map(product => {
        const valueCalculateWith = whichPropertyToUse === 'max' ? planBook.ApplicationMax
          : whichPropertyToUse === 'min' ? planBook.ApplicationMin
            : planBook.ApplicationRate;
        const translatedUnit = this.translationService.translate(this.unitStore?.getUnitWithUnit(product.Unit?.Id).Key);
        const calculationResult = this.roundNumericService.roundAsNumber(valueCalculateWith * product.ProductRate / planBook.ApplicationRate);
        return `${product.Product.ProductName} ${calculationResult} ${translatedUnit}`;
      }).join('<br>');
    } else {
      return dataItem.ErrorMessage;
    }
  }
}
